<script setup>
import { useI18n } from 'vue-i18n';
import { computed, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AuthShell from '@/components/auth/AuthShell.vue';
import AuthField from '@/components/auth/AuthField.vue';
import PasswordStrength from '@/components/auth/PasswordStrength.vue';
import UiButton from '@/components/ui/UiButton.vue';
import { useAuthStore } from '@/store/auth/index.js';
import { usePageMeta } from '@/composables/usePageMeta';
import { fieldErrors, passwordChecks } from '@/utils/auth/password';
import { requestError } from '@/utils/study/labels';

const auth = useAuthStore();
const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

usePageMeta(() => ({ title: t('study.auth.reset.metaTitle'), description: t('study.auth.reset.metaDesc'), path: '/reset-password', lang: locale.value }));

// From the email's link: /reset-password?token=…&email=…
const token = typeof route.query.token === 'string' ? route.query.token : '';
const email = typeof route.query.email === 'string' ? route.query.email : '';
const linkBroken = !token || !email;

const form = reactive({ password: '', password_confirmation: '' });
const errors = ref({});
const error = ref('');
const loading = ref(false);
const tried = ref(false);

const ready = computed(() => passwordChecks(form.password, form.password_confirmation).every((c) => c.ok));

async function submit() {
  tried.value = true;
  if (loading.value || !ready.value) return;
  errors.value = {};
  error.value = '';
  loading.value = true;
  try {
    await auth.resetPassword({ token, email, ...form });
    auth.clearSession();
    router.replace({ name: 'admin.login', query: { email, notice: 'reset' } });
  } catch (e) {
    errors.value = fieldErrors(e);
    if (!Object.keys(errors.value).length) error.value = requestError(e);
    else if (errors.value.token || errors.value.email) error.value = errors.value.token ?? errors.value.email;
  } finally {
    loading.value = false;
  }
}
</script>

<template>
  <AuthShell
    :badge="t('study.auth.panelBadge')"
    :home-label="t('study.auth.backToSite')"
    :title="t('study.auth.reset.title')"
    :accent="t('study.auth.reset.accent')"
    :lead="linkBroken ? '' : t('study.auth.reset.lead')"
    :card-title="linkBroken ? t('study.auth.reset.cardBroken') : t('study.auth.reset.cardSet')"
    :card-hint="linkBroken ? t('study.auth.reset.hintBroken') : t('study.auth.reset.hintAccount', { email })"
  >
    <UiButton v-if="linkBroken" :to="{ name: 'auth.forgot' }" size="lg" block>{{ t('study.auth.reset.requestLink') }}</UiButton>

    <form v-else class="space-y-5" novalidate @submit.prevent="submit">
      <div v-if="error" class="rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800" role="alert">
        <p class="flex items-start gap-3">
          <iconify-icon icon="solar:danger-circle-linear" class="text-lg shrink-0 mt-px" aria-hidden="true" />{{ error }}
        </p>
        <RouterLink :to="{ name: 'auth.forgot', query: { email } }" class="mt-2 ml-8 inline-block text-xs font-semibold underline underline-offset-4">{{ t('study.auth.reset.requestLink') }}</RouterLink>
      </div>

      <!-- Lets password managers save the new password under the right account. -->
      <input type="email" :value="email" autocomplete="username" class="sr-only" tabindex="-1" aria-hidden="true" readonly />
      <AuthField v-model="form.password" :label="t('study.auth.reset.newPassword')" type="password" autocomplete="new-password" :error="errors.password" required />
      <AuthField
        v-model="form.password_confirmation"
        :label="t('study.auth.reset.repeat')"
        type="password"
        autocomplete="new-password"
        :error="tried && form.password_confirmation !== form.password ? t('study.auth.reset.mismatch') : ''"
        required
      />
      <PasswordStrength :password="form.password" :confirmation="form.password_confirmation" />
      <UiButton type="submit" size="lg" block  :loading="loading" :class="!ready ? 'opacity-50' : ''" :aria-disabled="!ready">{{ t('study.auth.reset.submit') }}</UiButton>
    </form>
  </AuthShell>
</template>
